import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ArrowDownTrayIcon,
  TrashIcon,
  DocumentIcon,
} from '@heroicons/react/24/outline';
import Notification, { NotificationContainer } from '../../../components/Notification';
import contractDocumentsService from '../../../services/compliance/contractDocuments.service';
import { ContractDocument } from '../../../types/compliance';

interface Props {
  contractId: string;
}

function humanSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export default function ContractDocumentsPanel({ contractId }: Props) {
  const [docs, setDocs] = useState<ContractDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // Per-row in-flight state for inline description edits. We only save on
  // blur/Enter when the value actually changed, so local input state lives on
  // the uncontrolled DOM element — we just track "currently saving" rows here.
  const [savingIds, setSavingIds] = useState<Set<string>>(new Set());
  const [errorNotification, setErrorNotification] =
    useState<{ title: string; message: string } | null>(null);
  const [successNotification, setSuccessNotification] =
    useState<{ title: string; message: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await contractDocumentsService.list(contractId);
      setDocs(rows);
    } catch (error: any) {
      const msg = error?.response?.data?.detail || error?.message || 'Unknown error occurred';
      setErrorNotification({ title: 'Failed to load documents', message: msg });
    } finally {
      setLoading(false);
    }
  }, [contractId]);

  useEffect(() => {
    load();
  }, [load]);

  const doUpload = useCallback(
    async (file: File) => {
      setUploading(true);
      try {
        // Description is now edited inline in the table row after upload, so
        // we always upload with an empty description (the DB column is
        // NOT NULL DEFAULT '').
        await contractDocumentsService.upload(contractId, file, '');
        setSuccessNotification({
          title: 'Uploaded',
          message: `"${file.name}" uploaded successfully.`,
        });
        load();
      } catch (error: any) {
        const msg = error?.response?.data?.detail || error?.message || 'Unknown error occurred';
        setErrorNotification({ title: 'Failed to Upload', message: msg });
      } finally {
        setUploading(false);
      }
    },
    [contractId, load],
  );

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) doUpload(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) doUpload(file);
  };

  const handleDownload = async (doc: ContractDocument) => {
    try {
      await contractDocumentsService.download(contractId, doc.id, doc.original_filename);
    } catch (error: any) {
      const msg = error?.response?.data?.detail || error?.message || 'Unknown error occurred';
      setErrorNotification({ title: 'Failed to Download', message: msg });
    }
  };

  const handleDelete = async (doc: ContractDocument) => {
    if (!window.confirm(`Delete "${doc.original_filename}"?`)) return;
    try {
      await contractDocumentsService.remove(contractId, doc.id);
      load();
    } catch (error: any) {
      const msg = error?.response?.data?.detail || error?.message || 'Unknown error occurred';
      setErrorNotification({ title: 'Failed to Delete', message: msg });
    }
  };

  const saveDescription = async (doc: ContractDocument, newValue: string, input: HTMLInputElement) => {
    if (newValue === doc.description) return;
    setSavingIds((prev) => {
      const next = new Set(prev);
      next.add(doc.id);
      return next;
    });
    try {
      const updated = await contractDocumentsService.update(contractId, doc.id, newValue);
      // Optimistically update in place so a full reload isn't required.
      setDocs((prev) => prev.map((d) => (d.id === doc.id ? updated : d)));
    } catch (error: any) {
      const msg = error?.response?.data?.detail || error?.message || 'Unknown error occurred';
      setErrorNotification({ title: 'Failed to Update Description', message: msg });
      // Revert the input to the last known good value from server state.
      input.value = doc.description || '';
    } finally {
      setSavingIds((prev) => {
        const next = new Set(prev);
        next.delete(doc.id);
        return next;
      });
    }
  };

  return (
    <div>
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-lg p-6 text-center mb-4 transition-colors ${
          dragOver
            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
            : 'border-gray-300 dark:border-gray-600'
        }`}
      >
        <DocumentIcon className="w-10 h-10 mx-auto text-gray-400 mb-2" />
        <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
          Drag and drop a file here, or
        </p>
        <input
          ref={fileInputRef}
          type="file"
          onChange={handleFileInputChange}
          disabled={uploading}
          className="hidden"
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50"
        >
          {uploading ? 'Uploading...' : 'Choose file'}
        </button>
      </div>

      {loading ? (
        <div className="text-center py-6 text-gray-500">Loading documents...</div>
      ) : docs.length === 0 ? (
        <div className="text-center py-6 text-gray-500">No documents uploaded yet.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b border-gray-200 dark:border-gray-700">
                <th className="py-2 pr-4">Filename</th>
                <th className="py-2 pr-4">Size</th>
                <th className="py-2 pr-4">Uploaded</th>
                <th className="py-2 pr-4">Description</th>
                <th className="py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {docs.map((d) => {
                const isSaving = savingIds.has(d.id);
                return (
                  <tr key={d.id} className="border-b border-gray-100 dark:border-gray-700">
                    <td className="py-2 pr-4">{d.original_filename}</td>
                    <td className="py-2 pr-4">{humanSize(d.file_size)}</td>
                    <td className="py-2 pr-4">{formatDate(d.uploaded_at)}</td>
                    <td className="py-2 pr-4">
                      <input
                        type="text"
                        // Uncontrolled on purpose: defaultValue + key on doc.id
                        // so the input resets when the row's source value
                        // changes (e.g. after a successful save or revert).
                        key={`${d.id}-${d.description}`}
                        defaultValue={d.description || ''}
                        maxLength={255}
                        disabled={isSaving}
                        placeholder="Add description..."
                        aria-label={`Description for ${d.original_filename}`}
                        onBlur={(e) => saveDescription(d, e.target.value, e.target)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            (e.target as HTMLInputElement).blur();
                          } else if (e.key === 'Escape') {
                            e.preventDefault();
                            (e.target as HTMLInputElement).value = d.description || '';
                            (e.target as HTMLInputElement).blur();
                          }
                        }}
                        className={`w-full px-2 py-1 text-sm bg-transparent border rounded transition-colors ${
                          isSaving
                            ? 'italic text-gray-400 border-gray-200 dark:border-gray-700'
                            : 'border-transparent hover:border-gray-300 focus:border-blue-500 focus:bg-white dark:hover:border-gray-600 dark:focus:bg-gray-700 dark:text-white'
                        } focus:outline-none`}
                      />
                    </td>
                    <td className="py-2 text-right">
                      <div className="inline-flex gap-2">
                        <button
                          onClick={() => handleDownload(d)}
                          className="p-1 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                          title="Download"
                        >
                          <ArrowDownTrayIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(d)}
                          className="p-1 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                          title="Delete"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {errorNotification && (
        <NotificationContainer position="top-right">
          <Notification
            type="error"
            title={errorNotification.title}
            message={errorNotification.message}
            showClose={true}
            onClose={() => setErrorNotification(null)}
            autoDismiss={5000}
          />
        </NotificationContainer>
      )}

      {successNotification && (
        <NotificationContainer position="top-right">
          <Notification
            type="success"
            title={successNotification.title}
            message={successNotification.message}
            showClose={true}
            onClose={() => setSuccessNotification(null)}
            autoDismiss={3000}
          />
        </NotificationContainer>
      )}
    </div>
  );
}
